import type { BookingType, BookingTypeRules, VenueConfig } from "../../common/jsonRpcTypes";
import { formatTimeOfDay } from "../../utils/countryLocaleMap";
import { isDmnSource } from "./bookingSource";

// ─── Exported Interfaces ───

export interface TimeOption {
  value: string; // 'HH:mm'
  label: string; // Localised display text e.g. '19:00' (GB) or '7:00 PM' (US)
  available: boolean;
}

export interface DurationOption {
  value: number; // minutes
  label: string; // e.g. '19:30 (1h 30m)'
  /**
   * Marks the open-ended "until close" option (value -1). The label is not
   * translatable here (this module has no i18n context), so consumers render
   * their own translated text when this is set.
   */
  untilClose?: boolean;
}

/** Minimal booking details required by the utility functions. */
export interface BookingDetails {
  type?: string | { id: string; name: string };
  date?: string; // 'YYYY-MM-DD' or ISO date string
  time?: string; // 'HH:mm'
  num_people?: number;
  source?: string;
}

export interface BookableFromUntil {
  from: string; // 'HH:mm'
  until: string; // 'HH:mm'
  minDuration: number | false;
  maxDuration: number | false;
}

export interface DateBookableResult {
  bookable: boolean;
  message?: string;
}

// ─── Constants ───

/** Times before this hour are treated as belonging to the previous day (overnight handling). */
const DAY_CUTOFF = 7;

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;

// ─── Internal Helpers ───

/** Extract the booking type ID string from a type that may be a string or object. */
function getTypeId(type: string | { id: string; name: string } | undefined): string | undefined {
  if (!type) return undefined;
  return typeof type === "object" && type !== null ? type.id : type;
}

/** Parse a date string into a Date object at midnight UTC. */
function parseDate(date: string | undefined): Date {
  if (!date) return new Date();
  // Handle 'YYYY-MM-DD' format explicitly to avoid timezone issues
  const parts = date.split("T")[0].split("-");
  if (parts.length === 3) {
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 0, 0, 0, 0);
  }
  return new Date(date);
}

/** Get the lowercase weekday name for a given date. */
function getWeekday(date: Date): string {
  return WEEKDAYS[date.getDay()];
}

/** Format a Date to 'YYYY-MM-DD'. */
function formatDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Parse 'HH:mm' into total minutes from midnight. */
export function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

/** Convert total minutes from midnight to 'HH:mm'. */
function minutesToTime(mins: number): string {
  // Normalise to 0-1440 range for display
  const normalised = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(normalised / 60);
  const m = normalised % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Format minutes-from-midnight into a display label for the country's locale,
 * e.g. '19:00' for GB and '7:00 PM' for US.
 *
 * Delegates to the shared `formatTimeOfDay` formatter so the dropdown options
 * read the same way as the times shown elsewhere in the journey (booking
 * summary, cross-sell modal, confirmation).
 */
export function formatTimeLabel(mins: number, countryCode: string, locale?: string): string {
  return formatTimeOfDay(minutesToTime(mins), countryCode, locale);
}

/**
 * Adjust a minutes-from-midnight value for the day cutoff.
 * Times before the cutoff hour are treated as next-day (add 1440 minutes).
 */
function adjustForCutoff(mins: number): number {
  return mins < DAY_CUTOFF * 60 ? mins + 1440 : mins;
}

/** Find a booking type by ID within venue config. */
function findBookingType(venueConfig: VenueConfig, typeId: string | undefined): BookingType | undefined {
  if (!typeId) return undefined;
  return venueConfig.bookingTypes.find((bt) => bt.id === typeId);
}

/**
 * Get the opening times entry for a specific date, falling back to the weekday.
 *
 * The DMN API returns opening times as plain objects per weekday/date:
 *   { open: "09:00", close: "23:00", status: true, private_hire: false }
 * but they may also arrive as single-element arrays (legacy/test format):
 *   [{ open: "09:00", close: "23:00" }]
 *
 * This function normalises both shapes into a single entry.
 */
export function getOpeningTimesForDate(
  venueConfig: VenueConfig,
  dateString: string,
  weekday: string,
): { open: string; close: string; status?: boolean; private_hire?: boolean } | undefined {
  const openingTimes = venueConfig.openingTimes;
  if (!openingTimes) return undefined;

  // Check for date-specific opening times first, then fall back to weekday
  const key = openingTimes[dateString] ? dateString : weekday;
  const entries = openingTimes[key];

  if (!entries) return undefined;

  // Handle both object and array formats from the API
  if (Array.isArray(entries)) {
    return entries.length > 0 ? entries[0] : undefined;
  }

  // Plain object format (standard DMN API response)
  if (typeof entries === "object" && entries.open !== undefined) {
    return entries;
  }

  return undefined;
}

/** Build a today date at midnight (local time). */
function todayAtMidnight(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
}

// ─── Exported Functions ───

/**
 * Determine whether a booking's date/time falls within the next 24 hours.
 *
 * Ported from dmn/js/controllers/bookController.js (lines 256-264): the legacy
 * app shows a time warning when the booking is in the future but less than
 * 24 hours away. Returns true only when the combined datetime is after now AND
 * before now + 24 hours.
 *
 * @param date Date string ('YYYY-MM-DD' or ISO date string).
 * @param time Time string in 'HH:mm' format.
 */
export function isBookingWithin24Hours(date: string, time: string): boolean {
  if (!date || !time) return false;

  const [hours, minutes] = time.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;

  const bookingDateTime = parseDate(date);
  bookingDateTime.setHours(hours, minutes, 0, 0);

  const now = new Date();
  const in24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);

  return bookingDateTime > now && bookingDateTime < in24Hours;
}

/**
 * Determine whether a booking start time has passed in the local timezone.
 *
 * Booking dates and times are deliberately validated before the existing
 * parsing helpers are used so malformed form values cannot be treated as
 * expired.
 */
export function hasStartTimePassed(date: string, time: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return false;

  const parsedDate = parseDate(date);
  if (Number.isNaN(parsedDate.getTime()) || formatDateString(parsedDate) !== date) return false;

  const [hours, minutes] = time.split(":").map(Number);
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return false;
  }

  const today = todayAtMidnight();
  if (parsedDate.getTime() > today.getTime()) return false;
  if (parsedDate.getTime() < today.getTime()) return true;

  const current = new Date();
  const currentMins = current.getHours() * 60 + current.getMinutes();
  return timeToMinutes(time) < currentMins;
}

/**
 * Get booking rules for a specific date, checking overrides first then weekday defaults.
 *
 * Ported from dmn/js/services/bookingManager.js lines 505-549.
 *
 * 1. Check `booking_overrides` for date-specific rules (date ranges)
 * 2. Fall back to `booking_rules[weekday]` for the selected day
 * 3. For DesignMyNight sources, apply `booking_rules_dmn[weekday]` overrides
 */
/** Copy only the known booking-rule fields from a date override, excluding its metadata (id, date_from, date_to, booking_notes). */
function buildDateOverrideRules(override: NonNullable<BookingType["booking_overrides"]>[number]): BookingTypeRules {
  const result: BookingTypeRules = {};
  if (override.booking_available !== undefined) result.booking_available = override.booking_available;
  if (override.bookings_from !== undefined) result.bookings_from = override.bookings_from;
  if (override.bookings_to !== undefined) result.bookings_to = override.bookings_to;
  if (override.min_duration !== undefined) result.min_duration = override.min_duration;
  if (override.max_duration !== undefined) result.max_duration = override.max_duration;
  if (override.min_people !== undefined) result.min_people = override.min_people;
  if (override.max_people !== undefined) result.max_people = override.max_people;
  if (override.bookings_shut !== undefined) result.bookings_shut = override.bookings_shut;
  return result;
}

/** Find the first matching date-range override for a given date. */
export function findDateOverrideRules(bookingType: BookingType, parsedDate: Date): BookingTypeRules | null {
  if (!bookingType.booking_overrides) return null;

  for (const override of bookingType.booking_overrides) {
    if (!override.date_from || !override.date_to) continue;

    const dateFrom = parseDate(override.date_from);
    const dateTo = parseDate(override.date_to);

    if (parsedDate >= dateFrom && parsedDate <= dateTo) {
      return buildDateOverrideRules(override);
    }
  }
  return null;
}

/**
 * Get a booking type's DesignMyNight rules for a weekday, or `null` when they
 * do not apply.
 *
 * Mirrors `Venue::getBookingRules()` (dmn/classes/Venue.php lines 5494-5518):
 * the DMN rules only take effect for a DesignMyNight source, and only for days
 * the venue flagged with `override`.
 *
 * The flag is treated as "applies unless explicitly disabled" rather than
 * requiring `true`. `Venue::saveBookingTypes()` (Venue.php lines 3470-3507)
 * discards every day whose `override` is falsy before storing, so a persisted
 * day always carries `override: true`; demanding the key be present would let a
 * payload that omits it silently disable the overrides altogether.
 */
export function getDmnRulesForWeekday(
  bookingType: BookingType,
  weekday: string,
  source: string | undefined,
): BookingTypeRules | null {
  if (!isDmnSource(source)) return null;

  const dmnRules = bookingType.booking_rules_dmn?.[weekday];
  if (!dmnRules || dmnRules.override === false) return null;

  return dmnRules;
}

export function getRulesForDate(
  bookingType: BookingType | undefined,
  date: string | undefined,
  source?: string,
): BookingTypeRules {
  if (!bookingType) return {};

  const parsedDate = parseDate(date);
  const weekday = getWeekday(parsedDate);
  const bookingSource = source ?? "partner";

  const weekdayRules: BookingTypeRules = bookingType.booking_rules?.[weekday] ?? {};

  const overrideRules = findDateOverrideRules(bookingType, parsedDate);
  let mergedRules = overrideRules ? { ...weekdayRules, ...overrideRules } : { ...weekdayRules };

  const dmnRules = getDmnRulesForWeekday(bookingType, weekday, bookingSource);
  if (dmnRules) {
    mergedRules = { ...mergedRules, ...dmnRules };
  }

  return mergedRules;
}

/**
 * Get rules with `num_people_overrides` applied on top of date rules.
 *
 * Ported from dmn/js/services/bookingManager.js lines 494-503.
 */
export function getRulesForBooking(
  bookingType: BookingType | undefined,
  date: string | undefined,
  numPeople?: number,
  source?: string,
): BookingTypeRules {
  if (!bookingType) return {};

  const rulesForDate = getRulesForDate(bookingType, date, source);
  const numPeopleOverrides = getNumPeopleOverrides(bookingType, numPeople);

  return { ...rulesForDate, ...numPeopleOverrides };
}

/**
 * Find matching num_people_overrides for the given guest count.
 *
 * Ported from dmn/js/services/bookingManager.js lines 551-562.
 */
function getNumPeopleOverrides(bookingType: BookingType, numPeople: number | undefined): Partial<BookingTypeRules> {
  if (!numPeople || !bookingType.num_people_overrides) return {};

  for (const override of bookingType.num_people_overrides) {
    if (numPeople >= override.num_people_from && numPeople <= override.num_people_to) {
      const result: Partial<BookingTypeRules> = {};
      if (override.min_duration !== undefined) result.min_duration = override.min_duration;
      if (override.max_duration !== undefined) result.max_duration = override.max_duration;
      return result;
    }
  }

  return {};
}

/**
 * Get unavailable time ranges for a specific date and booking type.
 *
 * Ported from dmn/js/services/bookingManager.js lines 564-601.
 */
/** Shape of a single unavailability entry from the venue config. */
interface UnavailabilityEntry {
  unavailable_from: string;
  unavailable_to: string;
  unavailable_from_time?: string;
  unavailable_to_time?: string;
  unavailable_message?: string;
}

/** Convert an unavailability entry into a from/to minutes range, if the date falls within it. */
function toUnavailableTimeRange(entry: UnavailabilityEntry, dateAtMidnight: Date): { from: number; to: number } | null {
  const unavailableFrom = parseDate(entry.unavailable_from);
  const unavailableTo = parseDate(entry.unavailable_to);

  if (dateAtMidnight < unavailableFrom || dateAtMidnight > unavailableTo) return null;

  const fromMins = timeToMinutes(entry.unavailable_from_time ?? "00:00");
  let toMins = timeToMinutes(entry.unavailable_to_time ?? "00:00");

  if (toMins <= fromMins) {
    toMins = DAY_CUTOFF * 60 + 1440;
  }

  return { from: fromMins, to: toMins };
}

function getUnavailableTimesForDate(
  venueBookingUnavailable: Record<string, UnavailabilityEntry[]> | undefined,
  date: Date,
  typeId: string | undefined,
): Array<{ from: number; to: number }> {
  if (!venueBookingUnavailable) return [];

  const unavailableTimes: Array<{ from: number; to: number }> = [];
  const dateAtMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

  for (const [unavailableType, unavailableList] of Object.entries(venueBookingUnavailable)) {
    if (unavailableType !== "all" && unavailableType !== typeId) continue;
    if (!Array.isArray(unavailableList)) continue;

    for (const entry of unavailableList) {
      const range = toUnavailableTimeRange(entry, dateAtMidnight);
      if (range) {
        unavailableTimes.push(range);
      }
    }
  }

  return unavailableTimes;
}

/**
 * Get the bookable time window for a date.
 *
 * Ported from dmn/js/services/bookingManager.js lines 603-671.
 */
export function getBookableFromUntil(
  booking: BookingDetails,
  bookingType: BookingType | undefined,
  venueConfig: VenueConfig,
): BookableFromUntil {
  const typeId = getTypeId(booking.type);
  const bookingTypeDetails = bookingType ?? findBookingType(venueConfig, typeId);
  const parsedDate = parseDate(booking.date);
  const dateString = formatDateString(parsedDate);
  const weekday = getWeekday(parsedDate);

  // Get opening times
  const openingTimesEntry = getOpeningTimesForDate(venueConfig, dateString, weekday);
  const venueOpen = openingTimesEntry?.open ?? "09:00";
  const venueClose = openingTimesEntry?.close ?? "00:00";

  let venueOpenMins = timeToMinutes(venueOpen);
  let venueCloseMins = timeToMinutes(venueClose);

  // Adjust for overnight: if close is before open, it's next day
  venueOpenMins = adjustForCutoff(venueOpenMins);
  venueCloseMins = adjustForCutoff(venueCloseMins);
  if (venueCloseMins <= venueOpenMins) {
    venueCloseMins += 1440;
  }

  // Get booking rules
  const bookingRules = getRulesForBooking(bookingTypeDetails, booking.date, booking.num_people, booking.source);
  let bookingsFrom = bookingRules.bookings_from ?? venueOpen;
  const bookingsTo = bookingRules.bookings_to ?? venueClose;

  const minDuration = bookingRules.min_duration ?? false;
  const maxDuration = bookingRules.max_duration ?? false;

  // Don't allow bookings from before venue open (unless private hire)
  const bookingsFromMins = adjustForCutoff(timeToMinutes(bookingsFrom));
  if (bookingsFromMins < venueOpenMins && !bookingTypeDetails?.private_hire) {
    bookingsFrom = venueOpen;
  }

  // Don't allow bookings until after venue close (unless private hire)
  let bookingsUntilMins = adjustForCutoff(timeToMinutes(bookingsTo));
  if (bookingsUntilMins <= adjustForCutoff(timeToMinutes(venueOpen))) {
    bookingsUntilMins += 1440;
  }

  let bookingsUntil = bookingsTo;
  if (bookingsUntilMins > venueCloseMins && !bookingTypeDetails?.private_hire) {
    const applyMinDuration = (typeof minDuration === "number" && minDuration) || 15;
    const adjustedMins = venueCloseMins - applyMinDuration;
    bookingsUntil = minutesToTime(adjustedMins);
  }

  return { from: bookingsFrom, until: bookingsUntil, minDuration, maxDuration };
}

/**
 * Generate 15-minute time slots within the bookable window, marking each as available/unavailable.
 *
 * Ported from dmn/js/services/bookingManager.js lines 260-367.
 */
/** Parse the same-day bookings_shut minutes rule from booking rules. */
function parseSameDayMinutesRule(
  isSameDay: boolean,
  bookingRules: BookingTypeRules,
): { applies: boolean; minutes: number } {
  if (!isSameDay) return { applies: false, minutes: 0 };

  const bookingsShut = bookingRules.bookings_shut;
  if (typeof bookingsShut === "string" && bookingsShut.includes("mins")) {
    return {
      applies: true,
      minutes: Math.abs(Number.parseInt(bookingsShut, 10)),
    };
  }

  // Fall back to same_day_bookings_min_minutes
  if (bookingRules.same_day_bookings_min_minutes) {
    return {
      applies: true,
      minutes: bookingRules.same_day_bookings_min_minutes,
    };
  }

  return { applies: false, minutes: 0 };
}

/**
 * Parse an absolute bookings_shut time (e.g. "18:00") for same-day filtering.
 * Returns the shut time in minutes, or null if not applicable.
 */
function parseAbsoluteShutTime(isSameDay: boolean, bookingRules: BookingTypeRules): number | null {
  if (!isSameDay) return null;
  const bookingsShut = bookingRules.bookings_shut;
  if (typeof bookingsShut !== "string" || bookingsShut.includes("mins")) return null;
  // It's an absolute time like "18:00"
  const shutMins = timeToMinutes(bookingsShut);
  return shutMins;
}

/** Determine whether a time slot should be hidden and/or unavailable. */
function evaluateTimeSlot(
  currentMins: number,
  minDuration: number,
  unavailableTimes: Array<{ from: number; to: number }>,
  isSameDay: boolean,
  currentTimeInMins: number,
  sameDayRule: { applies: boolean; minutes: number },
  absoluteShutMins?: number | null,
): { hidden: boolean; unavailable: boolean } {
  const normalisedCurrent = currentMins >= 1440 ? currentMins - 1440 : currentMins;
  let unavailable = isSameDay && normalisedCurrent < currentTimeInMins;
  let hidden = unavailable;

  if (!unavailable) {
    unavailable = isTimeInUnavailableRange(currentMins, minDuration, unavailableTimes);
  }

  hidden = hidden || (sameDayRule.applies && currentTimeInMins + sameDayRule.minutes > normalisedCurrent);

  // Hide time slots at or after the absolute bookings_shut time
  if (absoluteShutMins != null && normalisedCurrent >= absoluteShutMins) {
    hidden = true;
  }

  if (hidden) {
    unavailable = true;
  }

  return { hidden, unavailable };
}

/** Check whether a time slot falls within any unavailable range. */
function isTimeInUnavailableRange(
  currentMins: number,
  minDuration: number,
  unavailableTimes: Array<{ from: number; to: number }>,
): boolean {
  for (const times of unavailableTimes) {
    const adjustedFrom = adjustForCutoff(times.from);
    const adjustedTo = adjustForCutoff(times.to);

    if (currentMins >= adjustedFrom && currentMins < adjustedTo) {
      return true;
    }
    if (minDuration) {
      const minUntilTime = currentMins + minDuration;
      if (minUntilTime > adjustedFrom && minUntilTime < adjustedTo) {
        return true;
      }
    }
  }
  return false;
}

export function getTimeOptions(
  booking: BookingDetails,
  bookingType: BookingType | undefined,
  venueConfig: VenueConfig,
  countryCode: string,
  locale?: string,
): TimeOption[] {
  if (!venueConfig.openingTimes) return [];

  const typeId = getTypeId(booking.type);
  const bookingTypeDetails = bookingType ?? findBookingType(venueConfig, typeId);
  const parsedDate = parseDate(booking.date);
  const today = todayAtMidnight();
  if (parsedDate.getTime() < today.getTime()) {
    // A date in the past has no bookable times.
    return [];
  }

  const bookingFromUntil = getBookableFromUntil(booking, bookingTypeDetails, venueConfig);
  const minDuration = typeof bookingFromUntil.minDuration === "number" ? bookingFromUntil.minDuration : 0;

  const timeInterval = bookingTypeDetails?.time_interval ?? 15;

  const unavailableTimes = getUnavailableTimesForDate(venueConfig.venueBookingUnavailable, parsedDate, typeId);

  let fromMins = adjustForCutoff(timeToMinutes(bookingFromUntil.from));
  let untilMins = adjustForCutoff(timeToMinutes(bookingFromUntil.until));
  if (untilMins <= fromMins) {
    untilMins += 1440;
  }

  const now = new Date();
  const isSameDay = formatDateString(parsedDate) === formatDateString(today);
  const currentTimeInMins = now.getHours() * 60 + now.getMinutes();

  const bookingRules = getRulesForBooking(bookingTypeDetails, booking.date, booking.num_people, booking.source);
  const sameDayRule = parseSameDayMinutesRule(isSameDay, bookingRules);
  const absoluteShutMins = parseAbsoluteShutTime(isSameDay, bookingRules);

  const options: TimeOption[] = [];
  let currentMins = fromMins;

  while (currentMins <= untilMins) {
    const slot = evaluateTimeSlot(
      currentMins,
      minDuration,
      unavailableTimes,
      isSameDay,
      currentTimeInMins,
      sameDayRule,
      absoluteShutMins,
    );

    if (!slot.hidden) {
      options.push({
        value: minutesToTime(currentMins),
        label: formatTimeLabel(currentMins, countryCode, locale),
        available: !slot.unavailable,
      });
    }

    currentMins += timeInterval;
  }

  return options;
}

/**
 * Generate 15-minute duration steps from min to max, respecting closing time.
 *
 * Ported from dmn/js/services/bookingManager.js lines 370-492.
 */
/** Find the first unavailable-close time that falls after the booking start. */
function findFirstUnavailableClose(
  unavailableTimes: Array<{ from: number; to: number }>,
  bookingTimeMins: number,
): number | undefined {
  for (const entry of unavailableTimes) {
    const adjustedFrom = adjustForCutoff(entry.from);
    if (adjustedFrom > bookingTimeMins) {
      return adjustedFrom;
    }
  }
  return undefined;
}

/** Check whether a duration falls within the min/max bounds. */
function isDurationInBounds(durationMins: number, minDuration: number, maxDuration: number): boolean {
  if (minDuration && durationMins < minDuration) return false;
  if (maxDuration && durationMins > maxDuration) return false;
  return true;
}

export function getDurationOptions(
  booking: BookingDetails,
  bookingType: BookingType | undefined,
  venueConfig: VenueConfig,
  countryCode: string,
  locale?: string,
): DurationOption[] {
  if (!venueConfig.openingTimes) return [];

  const typeId = getTypeId(booking.type);
  const bookingTypeDetails = bookingType ?? findBookingType(venueConfig, typeId);
  const parsedDate = parseDate(booking.date);

  const bookingFromUntil = getBookableFromUntil(booking, bookingTypeDetails, venueConfig);
  const minDuration = typeof bookingFromUntil.minDuration === "number" ? bookingFromUntil.minDuration : 0;
  const maxDuration = typeof bookingFromUntil.maxDuration === "number" ? bookingFromUntil.maxDuration : 0;

  const durationInterval = bookingTypeDetails?.duration_interval ?? 15;

  const openingTimesEntry = getOpeningTimesForDate(venueConfig, formatDateString(parsedDate), getWeekday(parsedDate));
  const venueOpen = openingTimesEntry?.open ?? "09:00";
  const venueClose = openingTimesEntry?.close ?? "00:00";

  let venueOpenMins = adjustForCutoff(timeToMinutes(venueOpen));
  let venueCloseMins = adjustForCutoff(timeToMinutes(venueClose));
  if (venueCloseMins <= venueOpenMins) {
    venueCloseMins += 1440;
  }

  const bookingTime = booking.time ?? "19:00";
  const bookingTimeMins = adjustForCutoff(timeToMinutes(bookingTime));

  const unavailableTimes = getUnavailableTimesForDate(venueConfig.venueBookingUnavailable, parsedDate, typeId);

  const unavailableClose = findFirstUnavailableClose(unavailableTimes, bookingTimeMins);
  const endTimeMins =
    unavailableClose !== undefined && unavailableClose < venueCloseMins ? unavailableClose : venueCloseMins;

  let durationMins = minDuration || durationInterval;
  let currentTimeMins = bookingTimeMins + durationMins;
  const options: DurationOption[] = [];

  while (currentTimeMins <= endTimeMins) {
    if (isDurationInBounds(durationMins, minDuration, maxDuration)) {
      options.push({
        value: durationMins,
        label: `${formatTimeLabel(currentTimeMins, countryCode, locale)} (${formatDurationLabel(durationMins)})`,
      });
    }

    durationMins += durationInterval;
    currentTimeMins += durationInterval;
  }

  const durationMinsUntilClose = endTimeMins - bookingTimeMins;
  if (!unavailableClose && (!maxDuration || durationMinsUntilClose <= maxDuration)) {
    options.unshift({ value: -1, label: "Until close", untilClose: true });
  }

  return options;
}

/** Format a duration in minutes to a readable label e.g. '1h 30m', '2h'. */
function formatDurationLabel(mins: number): string {
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;

  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
}

/**
 * Check if a date is bookable (venue open, not blocked, type available).
 *
 * Ported from dmn/js/services/bookingManager.js lines 673-878.
 */
export function isDateBookable(
  date: string,
  bookingType: BookingType | undefined,
  venueConfig: VenueConfig,
): DateBookableResult {
  const parsedDate = parseDate(date);
  const typeId = bookingType?.id;
  const bookingTypeDetails = bookingType ?? findBookingType(venueConfig, typeId);

  if (!bookingTypeDetails?.booking_rules) {
    return { bookable: false, message: "Bookings unavailable" };
  }

  const today = todayAtMidnight();
  const dateString = formatDateString(parsedDate);
  const currentDay = getWeekday(parsedDate);
  const typeName = bookingTypeDetails.name || "";

  if (parsedDate < today) {
    return { bookable: false };
  }

  const maxDaysResult = checkAdvanceMaxDays(parsedDate, today, bookingTypeDetails);
  if (maxDaysResult) return maxDaysResult;

  const minDaysResult = checkAdvanceMinDays(parsedDate, today, bookingTypeDetails);
  if (minDaysResult) return minDaysResult;

  const shutResult = checkSameDayBookingsShut(parsedDate, today, bookingTypeDetails, venueConfig, currentDay);
  if (shutResult) return shutResult;

  const openingResult = checkVenueOpeningStatus(venueConfig, dateString, currentDay);
  if (openingResult) return openingResult;

  const unavailableResult = checkVenueBookingUnavailable(venueConfig, parsedDate, typeId);
  if (unavailableResult) return unavailableResult;

  const bookingRules = getRulesForDate(bookingTypeDetails, date);
  if (bookingRules.booking_available === false) {
    return { bookable: false, message: `No ${typeName} bookings` };
  }

  return { bookable: true };
}

/** Format a Date to a friendly string e.g. '15 Mar'. */
function formatFriendlyDate(date: Date): string {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${date.getDate()} ${months[date.getMonth()]}`;
}

/** Check whether the date exceeds the advance_bookings_max_days limit. */
function checkAdvanceMaxDays(
  parsedDate: Date,
  today: Date,
  bookingTypeDetails: BookingType,
): DateBookableResult | null {
  const maxDays = bookingTypeDetails.advance_bookings_max_days;
  if (!maxDays) return null;

  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + maxDays);
  if (parsedDate > maxDate) {
    return { bookable: false, message: `Bookable up to ${formatFriendlyDate(maxDate)}` };
  }
  return null;
}

/** Check whether the date is before the advance_bookings_min_days limit. */
function checkAdvanceMinDays(
  parsedDate: Date,
  today: Date,
  bookingTypeDetails: BookingType,
): DateBookableResult | null {
  const minDays = bookingTypeDetails.advance_bookings_min_days;
  if (!minDays) return null;

  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + minDays);
  if (parsedDate < minDate) {
    return { bookable: false, message: `Must book ${minDays} days in advance` };
  }
  return null;
}

/** Check same-day bookings-shut time. Returns a result if bookings are shut, null otherwise. */
function checkSameDayBookingsShut(
  parsedDate: Date,
  today: Date,
  bookingTypeDetails: BookingType,
  venueConfig: VenueConfig,
  currentDay: string,
): DateBookableResult | null {
  if (formatDateString(parsedDate) !== formatDateString(today)) return null;

  const mergedRules = getRulesForDate(bookingTypeDetails, formatDateString(parsedDate));
  const bookingsShutRaw = mergedRules.bookings_shut;
  const bookingsShutTime = typeof bookingsShutRaw === "string" ? bookingsShutRaw : undefined;
  const venueBookingsShut = venueConfig.bookingsShut;
  const shutTime = bookingsShutTime ?? venueBookingsShut?.[currentDay]?.time;

  if (!shutTime) return null;

  const now = new Date();
  const shutParts = shutTime.split(":");
  const shutDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    Number(shutParts[0]),
    Number(shutParts[1]),
    0,
    0,
  );

  if (shutDate.getHours() < DAY_CUTOFF) {
    shutDate.setDate(shutDate.getDate() + 1);
  }

  if (shutDate < now) {
    const shutMessage = venueBookingsShut?.[currentDay]?.message;
    return { bookable: false, message: shutMessage ?? "Bookings unavailable" };
  }
  return null;
}

/** Check whether the venue is closed or has status=false for the date. */
function checkVenueOpeningStatus(
  venueConfig: VenueConfig,
  dateString: string,
  currentDay: string,
): DateBookableResult | null {
  const openingTimesEntry = getOpeningTimesForDate(venueConfig, dateString, currentDay);
  if (!openingTimesEntry) {
    return { bookable: false, message: "Closed" };
  }

  // If the venue has status=false for this day, it is closed.
  if (openingTimesEntry.status === false) {
    return { bookable: false, message: "Closed" };
  }

  return null;
}

/** Check whether the entire day is blocked by a venue-wide unavailability entry. */
function checkVenueBookingUnavailable(
  venueConfig: VenueConfig,
  parsedDate: Date,
  typeId: string | undefined,
): DateBookableResult | null {
  const venueBookingUnavailable = venueConfig.venueBookingUnavailable;

  if (!venueBookingUnavailable) return null;

  const dayStart = new Date(parsedDate);
  const dayEnd = new Date(parsedDate);
  dayEnd.setHours(23, 59, 0, 0);

  for (const [unavailableType, unavailableList] of Object.entries(venueBookingUnavailable)) {
    if (unavailableType !== "all" && unavailableType !== typeId) continue;
    if (!Array.isArray(unavailableList)) continue;

    for (const entry of unavailableList) {
      const unavailableFrom = parseDate(entry.unavailable_from);
      const unavailableTo = parseDate(entry.unavailable_to);
      unavailableTo.setHours(23, 59, 0, 0);

      if (dayStart >= unavailableFrom && dayEnd <= unavailableTo) {
        return {
          bookable: false,
          message: entry.unavailable_message ?? "Bookings unavailable",
        };
      }
    }
  }
  return null;
}
